import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import { Card, CardActionArea } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircleFlag } from "../../../common/CircleFlag";
import { useMatchCommentary } from "../../../common/contexts/aiCommentaryContext";
import { useTeamForm } from "../../../common/contexts/teamFormContext";
import { Bet } from "../../../types/Bet";
import { Match } from "../../../types/Match";
import { betOutcomeText } from "../../../utils/betScoring";
import { getMatchBettingState } from "../../../utils/betting";
import countryDict from "../../../utils/countryDict";
import {
    getDateFromDate,
    getLocalTimeFromDate,
} from "../../../utils/timeUtils";
import { matchVenueLabel } from "../../../utils/venue";
import BetOutcomeIcon from "../BetOutcomeIcon";
import CommentaryBadge from "../Commentary/CommentaryBadge";
import CommentaryModal from "../Commentary/CommentaryModal";
import "./FixtureTile.scss";
import TeamFormChips from "./TeamFormChips";

interface FixtureTileDesktopProps {
  match: Match;
  bet: Bet[];
  onClick: () => void;
}

const flagCode = (name?: string) => (name && countryDict.get(name)) || "pl";

const stageLabel = (match: Match) =>
  match.matchType.type === "Group"
    ? `Group ${match.matchType.value}`
    : match.matchType.value;

// Modern horizontal fixture tile for the desktop "Upcoming" view — shares the
// visual language of FixtureCard (BetCard family) but uses the wider layout.
// Adds the host venue (from the match's API stadium fields) and each team's
// recent form (derived from the loaded tournament matches).
const FixtureTileDesktop: React.FC<FixtureTileDesktopProps> = ({
  match,
  bet,
  onClick,
}) => {
  const navigate = useNavigate();
  const { getForm } = useTeamForm();
  const date = match.kickOffTime?.dateTime || "";
  const isPlayed = match.wasPlayed;
  const homeName = match.homeTeam.internationalName || "";
  const awayName = match.awayTeam.internationalName || "";
  const userBet = bet[0];
  const bettingState = getMatchBettingState(match);
  const bettingOpen = bettingState === "open";
  const matchAlreadyPlayed = bettingState === "played";

  const venue = matchVenueLabel(match);
  const homeForm = getForm(match.homeTeam.id);
  const awayForm = getForm(match.awayTeam.id);

  const { hasCommentary } = useMatchCommentary(match.id);
  const [commentaryOpen, setCommentaryOpen] = useState(false);

  const handleCommentaryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCommentaryOpen(true);
  };

  const handleBreakdownClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/match/${match.id}/bets-breakdown`);
  };

  return (
    <Card className="fixtureTileD" onClick={onClick}>
      <CardActionArea component="div" disableRipple className="fixtureTileD__action">
        <div className="fixtureTileD__headerRow">
          <div className="fixtureTileD__headerLeft">
            <span className="fixtureTileD__stage">{stageLabel(match)}</span>
            {hasCommentary && (
              <CommentaryBadge onClick={handleCommentaryClick} />
            )}
            {matchAlreadyPlayed && (
              <button
                type="button"
                className="fixtureCard__contextBtn fixtureCard__contextBtn--corner"
                onClick={handleBreakdownClick}
                aria-label="View all league bets for this match"
              >
                <FormatListBulletedOutlinedIcon />
                Bets
              </button>
            )}
          </div>
          <span className="fixtureTileD__datetime">
            <ScheduleRoundedIcon />
            {getDateFromDate(date)}
          </span>
        </div>

        {venue && (
          <div className="fixtureTileD__venue">
            <PlaceOutlinedIcon />
            {venue}
          </div>
        )}

        <div className="fixtureTileD__main">
          <div className="fixtureTileD__team fixtureTileD__team--home">
            <div className="fixtureTileD__teamMain">
              <span className="fixtureTileD__teamName">{homeName}</span>
              <span className="fixtureTileD__flag">
                <CircleFlag countryCode={flagCode(homeName)} height={44} width={44} />
              </span>
            </div>
            <TeamFormChips form={homeForm} align="right" />
          </div>

          <div className="fixtureTileD__center">
            {isPlayed ? (
              <span className="fixtureTileD__score">
                <span className="fixtureTileD__scoreNum">
                  {match.homeScoreRegular}
                </span>
                <span className="fixtureTileD__scoreSep">:</span>
                <span className="fixtureTileD__scoreNum">
                  {match.awayScoreRegular}
                </span>
              </span>
            ) : (
              <span className="fixtureTileD__time">
                {getLocalTimeFromDate(date)}
              </span>
            )}
          </div>

          <div className="fixtureTileD__team fixtureTileD__team--away">
            <div className="fixtureTileD__teamMain">
              <span className="fixtureTileD__flag">
                <CircleFlag countryCode={flagCode(awayName)} height={44} width={44} />
              </span>
              <span className="fixtureTileD__teamName">{awayName}</span>
            </div>
            <TeamFormChips form={awayForm} align="left" />
          </div>
        </div>

        {userBet ? (
          <div
            className={`fixtureTileD__footer${
              matchAlreadyPlayed ? " fixtureTileD__footer--withBreakdown" : ""
            }`}
          >
            {isPlayed ? (
              <span className="fixtureTileD__betChip fixtureTileD__betChip--result">
                <BetOutcomeIcon
                  outcome={userBet.betResult}
                  styles={{ fontSize: 16, margin: 0 }}
                />
                {userBet.homeTeamScoreBet}–{userBet.awayTeamScoreBet} ·{" "}
                {betOutcomeText(userBet.betResult)} · {userBet.points} pts
              </span>
            ) : (
              <span className="fixtureTileD__betChip">
                Your bet {userBet.homeTeamScoreBet}–{userBet.awayTeamScoreBet}
              </span>
            )}
          </div>
        ) : (
          <div
            className={`fixtureTileD__footer${
              matchAlreadyPlayed ? " fixtureTileD__footer--withBreakdown" : ""
            }`}
          >
            <span className="fixtureTileD__betChip fixtureTileD__betChip--empty">
              {bettingOpen
                ? "No bet yet - click to predict"
                : matchAlreadyPlayed
                ? "Match already played"
                : "You did not place a bet before kickoff."}
            </span>
          </div>
        )}
      </CardActionArea>

      <CommentaryModal
        open={commentaryOpen}
        onClose={() => setCommentaryOpen(false)}
        matchId={match.id}
        homeName={homeName}
        awayName={awayName}
      />
    </Card>
  );
};

export default FixtureTileDesktop;
